import { useMemo, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  dateStringToDate,
  formatDocumentIssueDate,
  formatDateTimeInTimeZone,
  formatTimeZoneLabel,
  normalizeDocumentDateFields,
} from '@/lib/date-values';
import {
  CreditCard,
  Loader2,
  Building2,
  Ban,
  FileText,
  CheckCircle,
  Clock3,
  BadgeCheck,
  HeartHandshake,
  ExternalLink,
  Landmark,
  Download,
} from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Separator } from '@/components/atoms/separator';
import { PublicLayout } from '@/components/public/PublicLayout';
import { DocumentNotFound } from '@/components/public/DocumentNotFound';
import { LineItemsTable } from '@/components/public/LineItemsTable';
import { PoweredByNotifi } from '@/components/public/PoweredByNotifi';
import { InvoiceDocumentRenderer } from '@/components/documents/invoices/InvoiceDocumentRenderer';
import type { PdfCompanyInfo } from '@/components/documents/invoices/InvoicePDF';
import { toInvoice } from '@/lib/invoices/publicInvoiceToInvoice';
import { ensureInvoiceJobBlocks } from '@/components/documents/estimates/templateBuilder/defaultLayout';
import { publicGet, publicPost, toPublicApiUrl } from '@/lib/api/publicClient';
import { isApiError } from '@/lib/api/errors';
import { formatAddress } from '@/lib/format-address';
import {
  type PublicInvoice,
  type CreateStripeSessionResponse,
  type PublicWisetackCta,
} from '@/types/public';
import { AchSavingsBadge } from '@/components/organisms/payments/AchSavingsBadge';
import { PayabliPaymentMethodSetupForm } from '@/components/organisms/payments/PayabliPaymentMethodSetupForm';
import {
  CustomerPayButton,
  PaymentRailSwitch,
} from '@/components/organisms/payments/PaymentRailSwitch';
import { WisetackDisclosure } from '@/components/shared/WisetackDisclosure';
import { WISETACK_BRAND } from '@/lib/wisetack/brand';
import { billToAddressLine, billToDisplayName } from '@/lib/invoices/billTo';
import {
  isPublicInvoicePaid,
  isPublicInvoicePayable,
  normalizePublicInvoiceStatus,
  publicPayableAmount,
} from '@/lib/invoices/publicInvoiceStatus';
import { customerClearingFootnote, shownInvoiceMoney } from '@/lib/invoices/shown-invoice-money';
import { PublicInvoiceProcessingNotice } from '@/components/public/PublicInvoiceProcessingNotice';
import { normalizePublicInvoiceLines } from '@/lib/invoices/publicInvoiceLines';
import { documentDiscountLabel } from '@/lib/sales/document-discount-label';
import { isHtmlContent } from '@/lib/utils';
import { resolveCustomerTerms, type ResolvedCustomerTerms } from '@/lib/terms/clientTerms';
import { CustomerTermsBody } from '@/components/public/CustomerTermsBody';
import { stripHtml } from '@/lib/strip-html';
import { cardServiceFeeNote } from '@/lib/payments/cardServiceFeeNote';
import {
  invoiceChargedTotalNote,
  invoicePaidInFullMessage,
  type InvoicePaidFigures,
} from '@/lib/invoices/invoiceTotalPaid';
import {
  computePaymentMethodDiscount,
  effectivePaymentMethodDiscountPercent,
  formatDiscountPercent,
} from '@/lib/payments/paymentMethodDiscount';
import { defaultPayabliRail, type PayabliRail } from '@/lib/payments/payabli';
import { isTipOverLimit, MAX_TIP_DOLLARS, TIP_LIMIT_MESSAGE } from '@/lib/payments/tipLimit';

type PublicInvoiceSearch = {
  payment?: 'success' | 'cancel';
};

export const Route = createFileRoute('/(public)/invoice/$id')({
  validateSearch: (search: Record<string, unknown>): PublicInvoiceSearch => ({
    payment:
      search.payment === 'success' || search.payment === 'cancel' ? search.payment : undefined,
  }),
  component: InvoicePage,
});

function asValidDate(value: unknown): Date | null {
  if (typeof value !== 'string' || value.trim().length === 0) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toExternalUrl(value: string | undefined) {
  if (!value) return undefined;
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

/**
 * What the customer was actually charged, stated under the paid thank-you.
 * Renders nothing when the card was charged exactly what the invoice was
 * credited — the invoice rows already say that.
 *
 * Exported so a test can drive it without the router-bound page, like
 * {@link PublicInvoiceIssueDate}.
 */
export function PublicInvoicePaidNote({ invoice }: { invoice: InvoicePaidFigures }) {
  const note = invoiceChargedTotalNote(invoice, formatMoney);
  if (!note) return null;
  return (
    <p className="text-center text-sm text-muted-foreground" data-testid="invoice-charged-total">
      {note}
    </p>
  );
}

const usd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

function formatMoney(amount: number): string {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

// SPEC-tz-6 — business INSTANTS (e.g. appointment times) render in the company
// tz (from the public payload), not the viewer's locale tz.
function formatDocDate(
  value: Date | string | null | undefined,
  timezone: string | null | undefined,
): string {
  if (!value) return '—';
  const iso = value instanceof Date ? value.toISOString() : value;
  return (
    formatDateTimeInTimeZone(iso, timezone ?? undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) || '—'
  );
}

// Date-only fields (due date — `@db.Date`, tz-less) render as the
// plain calendar day via a LOCAL-midnight parse, so they never shift to the
// previous day for viewers behind UTC. Do NOT run these through formatDocDate.
function formatDateOnly(value?: string | null): string {
  const d = dateStringToDate(value);
  return d
    ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(
        d,
      )
    : '—';
}

// Exported so the public-route regression test exercises the same payload pipeline.
// eslint-disable-next-line react-refresh/only-export-components
export function normalizePublicInvoice(raw: PublicInvoice): PublicInvoice {
  const status = normalizePublicInvoiceStatus(raw.status);

  const lineItems = normalizePublicInvoiceLines(raw.lineItems);

  const documentDates = normalizeDocumentDateFields(raw);

  return {
    ...raw,
    id: raw.id ?? '',
    status,
    lineItems,
    customFields: Array.isArray(raw.customFields) ? raw.customFields : [],
    subtotal: Number(raw.subtotal ?? 0),
    discountTotal: Number(raw.discountTotal ?? 0),
    membershipDiscountTotal: Number(raw.membershipDiscountTotal ?? 0),
    taxTotal: Number(raw.taxTotal ?? 0),
    tipAmount: Number(raw.tipAmount ?? 0),
    tipsEnabled: raw.tipsEnabled !== false,
    total: Number(raw.total ?? 0),
    amountPaid: Number(raw.amountPaid ?? 0),
    creditsApplied: Number(raw.creditsApplied ?? 0),
    balanceDue: Number(raw.balanceDue ?? 0),
    wisetack: raw.wisetack ?? null,
    date: documentDates.date,
    dueDate: raw.dueDate ?? undefined,
    createdAt: documentDates.createdAt,
    updatedAt: documentDates.updatedAt,
    business: raw.business
      ? {
          ...raw.business,
          address: formatAddress(raw.business.address),
        }
      : undefined,
    job: raw.job
      ? {
          ...raw.job,
          appointmentDates: Array.isArray(raw.job.appointmentDates) ? raw.job.appointmentDates : [],
        }
      : undefined,
    checklistReports: Array.isArray(raw.checklistReports)
      ? raw.checklistReports.map((report) => ({
          ...report,
          downloadUrl: toPublicApiUrl(report.downloadUrl),
        }))
      : [],
  };
}

export function PublicInvoiceIssueDate({
  invoice,
  timezone,
}: {
  invoice: Pick<PublicInvoice, 'date' | 'createdAt'>;
  timezone?: string | null;
}) {
  return <>{formatDocumentIssueDate(invoice.date, invoice.createdAt, timezone) || '-'}</>;
}

// Shown when the tenant has no usable online-payment path for this invoice
// (online payments disabled, an unsupported processor, or Payabli selected but
// its public config is missing) — so we never render a Pay button that would
// fail publicly or route to the wrong processor.
function PaymentUnavailableNotice() {
  return (
    <p className="text-center text-sm text-muted-foreground">
      Online payment is unavailable for this invoice. Please contact the business to pay it.
    </p>
  );
}

/**
 * Pre-payment tip prompt (percent chips of the balance due + custom amount).
 * Rendered in the payment section whenever tenant tipping is enabled. When an
 * online payment path is available, the gratuity rides the same charge instead
 * of requiring a second card entry on the post-payment tip page. Selecting
 * nothing charges exactly the balance.
 */
export function InvoiceTipPrompt({
  baseAmount,
  percents,
  tipCents,
  cardPriceDifference,
  cardOffered,
  achOrCheckOffered,
  creditCardOnly,
  achDiscountCents = 0,
  onChange,
}: {
  baseAmount: number;
  percents: number[];
  tipCents: number;
  cardPriceDifference: number;
  cardOffered: boolean;
  achOrCheckOffered: boolean;
  achDiscountCents?: number;
  /**
   * The card price difference rides CREDIT cards only and this surface cannot
   * yet tell (Payabli resolves funding at tokenization). Keeps the breakdown
   * from contradicting the pay button, which quotes the same figure as a
   * ceiling rather than a price. Required — see `cardServiceFeeNote`.
   */
  creditCardOnly: boolean;
  onChange: (cents: number) => void;
}) {
  const [customDollars, setCustomDollars] = useState('');
  const [customActive, setCustomActive] = useState(false);
  const baseCents = Math.round(baseAmount * 100);

  const applyCustom = (raw: string) => {
    setCustomDollars(raw);
    setCustomActive(true);
    const parsed = Math.round((parseFloat(raw || '0') || 0) * 100);
    onChange(parsed > 0 ? parsed : 0);
  };

  return (
    <div className="space-y-2">
      <p className="flex items-center gap-2 text-sm font-medium text-gray-700">
        <HeartHandshake className="h-4 w-4 text-pink-600" />
        Add a tip for your service team?
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant={!customActive && tipCents === 0 ? 'default' : 'outline'}
          onClick={() => {
            setCustomActive(false);
            setCustomDollars('');
            onChange(0);
          }}
        >
          No tip
        </Button>
        {percents.map((percent) => {
          const cents = Math.round((baseCents * percent) / 100);
          const selected = !customActive && tipCents === cents && cents > 0;
          return (
            <Button
              key={percent}
              type="button"
              size="sm"
              variant={selected ? 'default' : 'outline'}
              onClick={() => {
                setCustomActive(false);
                setCustomDollars('');
                onChange(cents);
              }}
            >
              {percent}% · {formatMoney(cents / 100)}
            </Button>
          );
        })}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-500">Custom</span>
        <span className="text-gray-500">$</span>
        <input
          type="number"
          min="0"
          max={MAX_TIP_DOLLARS}
          step="0.01"
          value={customDollars}
          onChange={(e) => applyCustom(e.target.value)}
          placeholder="0.00"
          className="w-24 rounded-md border border-gray-200 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      {/* Shown, never clamped: a percent tip on a large balance can pass the
          cap too, and silently changing the customer's figure is worse. */}
      {isTipOverLimit(tipCents) ? (
        <p role="alert" className="text-sm text-destructive">
          {TIP_LIMIT_MESSAGE}
        </p>
      ) : null}
      {tipCents > 0 ? (
        achDiscountCents > 0 && achOrCheckOffered ? (
          <div className="space-y-1 text-sm text-gray-600" data-testid="tip-payment-breakdown">
            <p>
              ACH/check total:{' '}
              <span className="font-semibold">
                {formatMoney(baseAmount + (tipCents - achDiscountCents) / 100)}
              </span>
            </p>
            {cardOffered ? (
              <p>
                Card total:{' '}
                <span className="font-semibold">{formatMoney(baseAmount + tipCents / 100)}</span>
              </p>
            ) : null}
          </div>
        ) : cardPriceDifference > 0 && cardOffered && achOrCheckOffered ? (
          <div className="space-y-1 text-sm text-gray-600" data-testid="tip-payment-breakdown">
            <p>
              ACH/check total:{' '}
              <span className="font-semibold">{formatMoney(baseAmount + tipCents / 100)}</span>
            </p>
            <p>
              {creditCardOnly ? 'Credit card total' : 'Card total'}:{' '}
              <span className="font-semibold">
                {formatMoney(baseAmount + tipCents / 100 + cardPriceDifference)}
              </span>
            </p>
          </div>
        ) : cardPriceDifference > 0 && cardOffered ? (
          <p className="text-sm text-gray-600" data-testid="tip-payment-breakdown">
            {creditCardOnly ? 'Total today (credit card)' : 'Total today'}:{' '}
            <span className="font-semibold">
              {formatMoney(baseAmount + tipCents / 100 + cardPriceDifference)}
            </span>{' '}
            ({formatMoney(baseAmount)} + {formatMoney(tipCents / 100)} tip
            {` + ${formatMoney(cardPriceDifference)} ${creditCardOnly ? 'credit ' : ''}card price difference)`}
          </p>
        ) : (
          <p className="text-sm text-gray-600" data-testid="tip-payment-breakdown">
            Invoice balance + tip:{' '}
            <span className="font-semibold">{formatMoney(baseAmount + tipCents / 100)}</span> (
            {formatMoney(baseAmount)} invoice balance + {formatMoney(tipCents / 100)} tip)
          </p>
        )
      ) : null}
      <p className="text-xs text-gray-500">
        A tip is added only when payment succeeds. Until then, it does not change this invoice or
        its PDF. Payment-method prices are shown in the payment controls.
      </p>
    </div>
  );
}

/** Exported as a router-independent seam for payment-availability regression tests. */
export function InvoicePrePaymentTipPrompt({
  payment,
  baseAmount,
  tipCents,
  cardPriceDifference = 0,
  cardOffered = false,
  achOrCheckOffered = false,
  onChange,
}: {
  payment: PublicInvoice['payment'] | null | undefined;
  baseAmount: number;
  tipCents: number;
  cardPriceDifference?: number;
  cardOffered?: boolean;
  achOrCheckOffered?: boolean;
  onChange: (cents: number) => void;
}) {
  if (payment?.tipping?.enabled !== true) {
    return null;
  }
  const surcharge =
    payment.processor === 'payabli' ? payment.payabli?.surcharge : payment.stripe?.surcharge;
  const discount = computePaymentMethodDiscount(surcharge, Math.round(baseAmount * 100), 'ACH');

  return (
    <InvoiceTipPrompt
      baseAmount={baseAmount}
      percents={payment.tipping.tipPercents}
      tipCents={tipCents}
      achDiscountCents={discount.enabled ? (surcharge?.discountCents ?? discount.discountCents) : 0}
      cardPriceDifference={cardPriceDifference}
      cardOffered={cardOffered}
      achOrCheckOffered={achOrCheckOffered}
      // Payabli charges the difference only when the tokenized card comes back
      // CREDIT. Stripe's hosted Checkout really does charge the quoted card
      // total (and refunds the fee later if the card was not credit), so its
      // breakdown keeps saying "Card total".
      creditCardOnly={payment.processor === 'payabli'}
      onChange={onChange}
    />
  );
}

/**
 * Payabli analog of the Stripe "Pay" button. Unlike Stripe (which redirects to
 * hosted Checkout), Payabli captures the card inline via the embedded component
 * and returns a temp token; we POST it to the invoice's payabli-charge route,
 * which charges the invoice's own balance server-side, then refetch so the page
 * flips to the paid state. The customer never sends an amount — only a token
 * (plus the optional tip in cents, validated server-side).
 */
export function PayabliInvoicePayment({
  invoiceId,
  amount,
  tipCents,
  config,
  contact,
  disabled,
}: {
  invoiceId: string;
  amount: number;
  /** Pre-payment gratuity in cents (0 = none), validated server-side. */
  tipCents: number;
  config: NonNullable<NonNullable<PublicInvoice['payment']>['payabli']>;
  contact?: PublicInvoice['contact'];
  disabled?: boolean;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  // The embedded form re-enables its own submit button as soon as tokenization
  // succeeds — before the server-side charge below finishes. Track our own
  // in-flight state and replace the form with a processing view while the
  // charge runs, so a second click can't tokenize again and fire a second
  // charge (a new token → a different idempotency key → not deduped server-side).
  const [charging, setCharging] = useState(false);
  // An ACH charge comes back HTTP 200 with status PENDING — the invoice stays
  // payable and this form stays mounted, so without a terminal state of its
  // own the page would spin on "Processing…" forever.
  const [pendingSettlement, setPendingSettlement] = useState(false);

  // The rail the embedded form is showing. Both rails are now reachable (the
  // form renders the switch), so the fee, the amount on the button and the
  // disclosure attestation all follow the payer's choice instead of assuming
  // the card.
  const [rail, setRail] = useState<PayabliRail>(() =>
    defaultPayabliRail({ card: config.acceptCard, ach: config.acceptACH }),
  );

  const surcharge = config.surcharge;
  const discount = computePaymentMethodDiscount(surcharge, Math.round(amount * 100), 'ACH');
  const discountCents = discount.enabled ? (surcharge?.discountCents ?? discount.discountCents) : 0;
  const achSavings =
    config.acceptACH && discount.enabled && discountCents > 0 ? (
      <AchSavingsBadge
        discountCents={discountCents}
        percent={discount.percent}
        hasTip={tipCents > 0}
      />
    ) : null;
  const feeText =
    !discount.enabled && surcharge?.enabled === true
      ? cardServiceFeeNote({
          ...surcharge,
          discountCents,
          discountAmount: formatMoney(discountCents / 100),
          surchargePercent: surcharge?.surchargePercent ?? 0,
          feeAmount: surcharge?.feeCents ? formatMoney(surcharge?.feeCents / 100) : null,
          achOrCheckPrice: amount + tipCents / 100,
          cardPriceDifference: surcharge?.feeCents == null ? null : surcharge?.feeCents / 100,
          cardOffered: config.acceptCard,
          achOffered: config.acceptACH,
          selectedRail: rail,
          // Nothing here knows whether the card is credit yet, and the charge
          // only ever adds the fee when Payabli's BIN data says it is.
          creditCardOnly: true,
        })
      : '';
  const feeNote = feeText ? <p className="text-center text-xs text-gray-500">{feeText}</p> : null;
  // The fee is part of the total the buttons must show for the rail in play —
  // "Pay $108" charging $111 is a disclosure bug — and it is never charged on
  // the bank rail (#2763).
  const railFeeAmount =
    !discount.enabled && surcharge?.enabled === true && config.acceptCard && rail === 'card'
      ? (surcharge?.feeCents ?? 0) / 100
      : 0;
  const totalAmount =
    amount + tipCents / 100 + railFeeAmount - (rail === 'ach' ? discountCents / 100 : 0);
  // Funding is resolved from the temp token AFTER this button is clicked, and
  // `settlePublicPayabliRowCharge` adds the fee only for CREDIT — a debit card,
  // a prepaid card, or a card whose BIN lookup came back empty is charged
  // `amount + tip`. So on the card rail this figure is a ceiling, and quoting
  // it flat ("Pay $512.75") asks the payer to consent to a total the server
  // will not charge them. A ceiling is the honest pre-commitment claim: it can
  // never undercharge the disclosure, and the note below says who pays less.
  const payLabel =
    railFeeAmount > 0 ? `Pay up to ${formatMoney(totalAmount)}` : `Pay ${formatMoney(totalAmount)}`;

  if (!config.publicToken || !config.entryPoint || !contact?.id) {
    // No payor id means the paypoint rejects tokenization client-side (an
    // opaque customerError AFTER the payer has typed a full card) and the
    // server-side charge has no contact to bind the stored method to. An
    // invoice can legitimately have no contact, so fail closed here the way
    // every other Payabli surface does rather than rendering a dead form.
    return (
      <p className="text-center text-sm text-muted-foreground">
        Online payment is temporarily unavailable. Please contact the business to pay this invoice.
      </p>
    );
  }

  if (pendingSettlement) {
    // Transient only: the awaited refetch in onComplete hands the settling
    // state to the page (server-derived `paymentAttemptSettling`), whose
    // banner replaces this form.
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
        <p className="font-semibold">Payment submitted</p>
        <p className="mt-0.5 text-sm opacity-90">
          Your bank transfer is processing. The invoice will update automatically once it finishes
          processing — no further action is needed.
        </p>
      </div>
    );
  }

  if (!open) {
    return (
      <>
        <Button
          className={`w-full ${achSavings ? 'h-auto min-h-11 flex-col gap-1.5 whitespace-normal py-3' : ''}`}
          size="lg"
          onClick={() => setOpen(true)}
          disabled={disabled}
        >
          <span className="flex items-center justify-center">
            <CreditCard className="mr-2 h-4 w-4" />
            {payLabel}
          </span>{' '}
          {achSavings}
        </Button>
        {feeNote}
      </>
    );
  }

  if (charging) {
    return (
      <div className="flex flex-col items-center gap-2 py-6">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        <p className="text-sm text-muted-foreground">Processing your payment…</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <PayabliPaymentMethodSetupForm
        appearance="customer"
        holderName={[contact.firstName, contact.lastName].filter(Boolean).join(' ') || undefined}
        publicToken={config.publicToken}
        entryPoint={config.entryPoint}
        mode={config.mode}
        submitLabel={payLabel}
        achSavings={achSavings}
        // Only render the rails the tenant actually accepts, so a public payer
        // never enters (e.g.) bank details the server-side charge would reject.
        // The backend always sends these (mirrors the charge-time gate), so
        // there's no client default that could re-expose a disabled rail.
        methods={{ card: config.acceptCard, ach: config.acceptACH }}
        onRailChange={setRail}
        errorTitle="Payment failed"
        // Paypoints that require a customer identifier reject tokenization
        // without a payor; customerNumber must be the contact id — the
        // server-side charge exchanges the temp token binding the same id.
        // Guaranteed present: the gate above refuses to render without it.
        customerData={{
          customerNumber: contact.id,
          firstName: contact.firstName || undefined,
          lastName: contact.lastName || undefined,
          billingEmail: contact.email || undefined,
        }}
        onComplete={async (result) => {
          setCharging(true);
          try {
            const charge = await publicPost<{ status?: string }>(
              `/api/public/invoices/${invoiceId}/payabli-charge`,
              {
                temporaryToken: result.temporaryToken,
                ...(tipCents > 0 ? { tipCents } : {}),
                // Attest the fee note above was rendered — the backend only
                // ever adds the card service fee when the page disclosed it.
                // Never on the bank rail: that payer was shown "no fee".
                ...(surcharge?.enabled === true && config.acceptCard && rail === 'card'
                  ? { surchargeDisclosed: true }
                  : {}),
              },
            );
            // Trust the settled status, not the 200 — a soft decline comes
            // back as a persisted FAILED row with HTTP 200, and an ACH charge
            // comes back PENDING. Neither is "successful".
            const status = charge?.status?.toUpperCase();
            if (status !== 'SUCCEEDED' && status !== 'PENDING') {
              throw new Error('Payment could not be completed. Please try another payment method.');
            }
            if (status === 'PENDING') {
              // Refresh BEFORE reporting pending: the page derives its
              // settling banner (and its poll) from the payload's
              // server-side `paymentAttemptSettling`, which this fetch
              // makes current.
              await queryClient.invalidateQueries({
                queryKey: ['public', 'invoice', invoiceId],
              });
              setPendingSettlement(true);
              setCharging(false);
              toast.success('Payment submitted.');
            } else {
              toast.success('Payment successful. Thank you!');
              // Refetch flips the page to its paid state, unmounting this form.
              await queryClient.invalidateQueries({
                queryKey: ['public', 'invoice', invoiceId],
              });
            }
          } catch (error) {
            // Re-enable the form so the customer can retry with a fresh entry.
            setCharging(false);
            toast.error(
              error instanceof Error && error.message
                ? error.message
                : 'Payment could not be completed. Please try again.',
            );
            throw error;
          }
        }}
      />
      {feeNote}
      <Button variant="ghost" className="w-full" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </div>
  );
}

function InvoicePage() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const [redirecting, setRedirecting] = useState(false);
  // Pre-payment gratuity (cents). Rides the same charge as the balance.
  const [tipCents, setTipCents] = useState(0);
  // Only a failure that follows an observed ACH settlement (this tab watched
  // the attempt) gets the failure banner — a cold load of an invoice whose
  // last payment failed is just a payable invoice.
  const sawSettlingRef = useRef(false);
  const settlementToken = useMemo(
    () => (search.payment === 'success' ? `${id}:${search.payment}` : null),
    [id, search.payment],
  );

  const {
    data: invoice,
    isLoading,
    isError,
  } = useQuery<PublicInvoice>({
    queryKey: ['public', 'invoice', id, settlementToken],
    queryFn: async () =>
      normalizePublicInvoice(
        await publicGet<PublicInvoice>(
          `/api/public/invoices/${id}`,
          settlementToken ? { _ts: settlementToken } : undefined,
        ),
      ),
    retry: false,
    refetchInterval: (query) => {
      const current = query.state.data as PublicInvoice | undefined;
      // Inline Payabli ACH still clearing (SERVER-derived, so a reload
      // resumes the poll too): poll until the webhook settles it — the page
      // flips to paid, or the attempt goes FAILED and the banner branches to
      // its failure state.
      if (current?.paymentAttemptSettling === true) {
        return 4000;
      }
      if (search.payment !== 'success') {
        return false;
      }
      return current && current.balanceDue <= 0 ? false : 1500;
    },
    refetchOnWindowFocus: search.payment === 'success',
  });
  const achSettling = invoice?.paymentAttemptSettling === true;
  if (achSettling) sawSettlingRef.current = true;
  const achFailed =
    !achSettling &&
    sawSettlingRef.current &&
    invoice?.latestPaymentStatus?.toUpperCase() === 'FAILED';

  const payMutation = useMutation({
    mutationFn: ({ tip, method }: { tip: number; method?: 'card' | 'ach' }) =>
      publicPost<CreateStripeSessionResponse>(`/api/public/invoices/${id}/stripe-session`, {
        ...(tip > 0 ? { tipCents: tip } : {}),
        ...(method ? { method } : {}),
      }),
    onSuccess: (data) => {
      // Redirect to Stripe-hosted Checkout page
      const url =
        data.url ?? (data.sessionId ? `https://checkout.stripe.com/pay/${data.sessionId}` : null);
      if (url) {
        setRedirecting(true);
        window.location.href = url;
      } else {
        toast.error('We couldn’t open the secure checkout. Please try again.');
      }
    },
    onError: (error) => {
      // A 4xx is server-authored copy written for the payer — above all "a
      // payment on this invoice is still processing", which is the whole
      // answer, where the generic line tells them to keep trying. Transport
      // noise (5xx, network) stays hidden, as on the quote deposit.
      const serverMessage =
        isApiError(error) && error.status >= 400 && error.status < 500 ? error.message.trim() : '';
      toast.error(serverMessage || 'Failed to initiate payment. Please try again.');
    },
  });

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </PublicLayout>
    );
  }

  if (isError || !invoice) {
    return <DocumentNotFound message="Invoice not found" />;
  }

  // NOT-277 — same guard the public quote and both PDFs apply: an unfilled
  // fill-in-the-blank clause ("a deposit of [__]%") must not reach the customer.
  const terms = resolveCustomerTerms(invoice.terms);

  // The public normalizer maps both VOID and WRITTEN_OFF API values to
  // `void`, so terminal documents share the same non-payable presentation.
  const isTerminal = invoice.status === 'void';
  // Shown figures: the payer's own bank money still processing counts as
  // paid. `isPaid` / `isPayable` below keep the STORED status and balance —
  // a processing payment is not a paid invoice until the bank settles.
  const shown = shownInvoiceMoney(invoice);
  const paidFootnote = isTerminal
    ? null
    : customerClearingFootnote(invoice, usd, invoice.timezone || 'UTC');
  const payNow = isTerminal ? 0 : publicPayableAmount(invoice);
  const displayedBalanceDue = isTerminal ? 0 : (shown.balance ?? invoice.balanceDue);
  const isPaid = isPublicInvoicePaid(invoice.status, invoice.balanceDue);
  const isPayable = isPublicInvoicePayable(invoice.status, invoice.balanceDue);
  const dueDate = asValidDate(invoice.dueDate);
  const timezone = invoice.timezone ?? undefined;
  const timezoneLabel = formatTimeZoneLabel(timezone);
  const isOverdue = (() => {
    // Overdue needs something still owed: a balance fully covered by a bank
    // payment in process is not late, whatever the stored status says.
    if (isPaid || isTerminal || !invoice.dueDate || displayedBalanceDue <= 0) return false;
    const due = new Date(invoice.dueDate);
    if (Number.isNaN(due.getTime())) return false;
    return due.getTime() < Date.now();
  })();
  // Nothing more is owed but the payer's bank payment has not settled: the
  // chip reads Clearing, as the portal and the staff screens do.
  const isClearing = !isTerminal && !isPaid && displayedBalanceDue <= 0 && shown.clearingNote > 0;
  const latestPaymentStatus = invoice.latestPaymentStatus?.toUpperCase();
  const isDisputedAfterRedirect =
    search.payment === 'success' &&
    (latestPaymentStatus === 'DISPUTED' ||
      latestPaymentStatus === 'DISPUTE_UNDER_REVIEW' ||
      latestPaymentStatus === 'CHARGEBACK_LOST') &&
    !isPaid;
  const isFailedAfterRedirect =
    search.payment === 'success' && latestPaymentStatus === 'FAILED' && !isPaid;
  const isSettlingPayment =
    search.payment === 'success' && isPayable && !isDisputedAfterRedirect && !isFailedAfterRedirect;
  // The server refuses a tip over the cap; the tip prompt explains why.
  const tipOverLimit = isTipOverLimit(tipCents);
  const contactName = [invoice.contact?.firstName, invoice.contact?.lastName]
    .filter(Boolean)
    .join(' ');
  // "Billed to" follows the resolved bill-to snapshot (override email/address already
  // applied), matching the PDF/panel. Self may still resolve to an additional
  // person; only a missing snapshot falls back to the direct contact.
  const billToSnapshot = invoice.billToContact;
  const showResolvedBillTo = Boolean(billToSnapshot);
  const billedToName = showResolvedBillTo ? billToDisplayName(billToSnapshot) : contactName;
  const billedToCompany = showResolvedBillTo ? '' : (invoice.contact?.company ?? '');
  const billedToEmail = showResolvedBillTo
    ? (billToSnapshot?.email ?? '')
    : (invoice.contact?.email ?? '');
  const billedToAddress = showResolvedBillTo ? billToAddressLine(billToSnapshot) : '';
  const businessWebsiteUrl = toExternalUrl(invoice.business?.website);
  const businessAddress = formatAddress(invoice.business?.address);
  const invoiceFooterText = invoice.business?.invoiceFooterText?.trim();
  const hasCustomInvoiceFooterText =
    Boolean(invoiceFooterText) && invoiceFooterText !== 'Powered by Notifi';
  const jobAppointmentDates = (invoice.job?.appointmentDates ?? [])
    .map((date) => asValidDate(date))
    .filter((date): date is Date => Boolean(date));

  // A resolved INVOICE document design (backend gates its presence on the tenant
  // toggle, exactly like the estimate link) routes the customer's page through
  // the SAME block engine as the invoice PDF, so the hosted link and the download
  // match. The public payload carries no payment ledger, so the paymentHistory
  // block — which could never populate here — is dropped, mirroring the PDF route.
  const designLayout =
    invoice.resolvedDocLayout &&
    Array.isArray(invoice.resolvedDocLayout.blocks) &&
    invoice.resolvedDocLayout.blocks.length > 0
      ? // Older invoice designs predate the `jobAddress` (service address) block;
        // inject it so the service address renders (same normalization the PDF uses).
        ensureInvoiceJobBlocks({
          ...invoice.resolvedDocLayout,
          blocks: invoice.resolvedDocLayout.blocks.filter((b) => b.type !== 'paymentHistory'),
        })
      : null;

  // Status chrome, the pay controls, checklist downloads and the footer are
  // app-owned — they render identically under the classic layout and the design,
  // so they live in shared nodes used by BOTH branches and can never diverge (the
  // exact drift this fix removes between the hosted page and the PDF).
  const statusBanners = (
    <>
      {isOverdue && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900">
          <div className="flex items-start gap-3">
            <Clock3 className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <div className="min-w-0">
              <p className="font-semibold">This invoice is past due</p>
              <p className="mt-0.5 text-sm opacity-90">
                Due on {formatDateOnly(invoice.dueDate)}. Please complete payment to avoid late
                fees.
              </p>
            </div>
          </div>
        </div>
      )}

      {isTerminal && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-700">
          <div className="flex items-start gap-3">
            <Ban className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" />
            <div className="min-w-0">
              <p className="font-semibold">This invoice is no longer payable</p>
              <p className="mt-0.5 text-sm text-gray-600">
                Payment is no longer available through this invoice.
              </p>
            </div>
          </div>
        </div>
      )}

      {search.payment === 'success' && !isTerminal && (
        <div
          className={`rounded-lg border px-4 py-3 ${
            isPaid
              ? 'border-green-200 bg-green-50 text-green-800'
              : isDisputedAfterRedirect
                ? 'border-orange-200 bg-orange-50 text-orange-900'
                : isFailedAfterRedirect
                  ? 'border-red-200 bg-red-50 text-red-900'
                  : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
        >
          <div className="flex items-start gap-3">
            {isPaid ? (
              <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
            ) : isDisputedAfterRedirect ? (
              <Clock3 className="mt-0.5 h-5 w-5 shrink-0 text-orange-600" />
            ) : isFailedAfterRedirect ? (
              <Clock3 className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            ) : (
              <Clock3 className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            )}
            <div className="min-w-0">
              <p className="font-semibold">
                {isPaid
                  ? 'Payment successful'
                  : isDisputedAfterRedirect
                    ? 'Payment disputed'
                    : isFailedAfterRedirect
                      ? 'Payment failed'
                      : 'Payment received, finalizing invoice'}
              </p>
              <p className="mt-0.5 text-sm opacity-90">
                {isPaid
                  ? invoicePaidInFullMessage(invoice, formatMoney)
                  : isDisputedAfterRedirect
                    ? latestPaymentStatus === 'CHARGEBACK_LOST'
                      ? 'This payment was reversed after a dispute. The invoice has an outstanding balance and can be paid again.'
                      : latestPaymentStatus === 'DISPUTE_UNDER_REVIEW'
                        ? 'This payment is being disputed. The invoice stays outstanding until the dispute is resolved.'
                        : 'This payment was disputed. The invoice still has an outstanding balance and can be paid again.'
                    : isFailedAfterRedirect
                      ? 'The payment didn’t go through. The invoice still has an outstanding balance, and you can try again.'
                      : 'Payment received. We’re updating your invoice now, which can take a few seconds.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {search.payment === 'cancel' && !isTerminal && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-slate-700">
          <p className="font-semibold">Checkout canceled</p>
          <p className="mt-0.5 text-sm">
            No payment was taken. You can try again whenever you are ready.
          </p>
        </div>
      )}
    </>
  );

  const checklistSection =
    invoice.checklistReports && invoice.checklistReports.length > 0 ? (
      <>
        <Separator />
        <div>
          <CardTitle className="mb-3 flex items-center gap-2 text-base">
            <BadgeCheck className="h-4 w-4" />
            Checklist Reports
          </CardTitle>
          <div className="space-y-2">
            {invoice.checklistReports.map((report) =>
              report.downloadUrl ? (
                <a
                  key={report.id}
                  href={report.downloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <span className="font-medium">{report.name}</span>
                  <ExternalLink className="h-4 w-4 text-gray-400" />
                </a>
              ) : (
                <div
                  key={report.id}
                  className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm text-gray-500"
                >
                  <span>{report.name}</span>
                  <span className="text-xs">Pending PDF</span>
                </div>
              ),
            )}
          </div>
        </div>
      </>
    ) : null;

  const tipPaymentRail =
    invoice.payment?.processor === 'payabli'
      ? invoice.payment.payabli
      : invoice.payment?.processor === 'stripe'
        ? invoice.payment.stripe
        : null;
  // Stripe Checkout cannot take a partial beside a bank payment in process,
  // so there is nothing to tip on until that payment finishes.
  const stripeAwaitingClearing =
    (!invoice.payment || invoice.payment.processor === 'stripe') && payNow < invoice.balanceDue;
  const tipCardPriceDifference =
    tipPaymentRail?.surcharge?.enabled === true
      ? (tipPaymentRail.surcharge?.feeCents ?? 0) / 100
      : 0;

  // Financing is an alternative for the outstanding balance, not a control
  // below the payment form. Keep the existing eligibility in both layouts.
  // The loan is priced on the STORED balance, so it is never offered while
  // bank money is in flight (`achSettling`) or the payable amount has been
  // netted below that balance: it would finance money already being paid.
  const financing =
    isPayable && !achSettling && payNow >= invoice.balanceDue && invoice.wisetack ? (
      <div className="border-t pt-4">
        <WisetackInvoiceCta wisetack={invoice.wisetack} />
      </div>
    ) : null;

  const payPanel = (
    <>
      {!isTerminal && shown.clearingNote > 0 ? (
        <>
          <Separator />
          <PublicInvoiceProcessingNotice invoice={invoice} />
        </>
      ) : null}
      {isPayable && payNow <= 0 && !(shown.clearingNote > 0) && (
        <>
          <Separator />
          {/* Server-derived: a payment reached the provider and is still
              being confirmed. Bank money clearing shows the processing notice
              above, so this is a card (or other) outcome: not "bank transfer".
              Never a live pay UI over money in flight — the poll above
              resolves this to paid or failed. */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
            <p className="font-semibold">Payment submitted</p>
            <p className="mt-0.5 text-sm opacity-90">
              Your payment is processing. The invoice will update automatically once it completes —
              no further action is needed.
            </p>
          </div>
        </>
      )}
      {isPayable && payNow > 0 && (
        <>
          <Separator />
          {achFailed ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-900">
              <p className="font-semibold">Payment failed</p>
              <p className="mt-0.5 text-sm opacity-90">
                Your bank transfer could not be completed. The invoice still has an outstanding
                balance — you can try again below with another payment method.
              </p>
            </div>
          ) : null}
          {/* Ask for the tip BEFORE payment so it rides the same charge, and
              only when no tip exists yet; the paid state below keeps the
              after-payment prompt for everyone who skipped it here.
              The tenant switch is NOT re-checked here: InvoicePrePaymentTipPrompt
              gates on payment.tipping.enabled, which the server derives from the
              same billing.paymentMethods.tipsEnabled as invoice.tipsEnabled.
              Testing both let the prompt hinge on two fields that can only ever
              agree — and hid which one actually decides. */}
          {invoice.tipAmount <= 0 && !stripeAwaitingClearing ? (
            <InvoicePrePaymentTipPrompt
              payment={invoice.payment}
              baseAmount={payNow}
              tipCents={tipCents}
              cardPriceDifference={tipCardPriceDifference}
              cardOffered={tipPaymentRail?.acceptCard === true}
              achOrCheckOffered={tipPaymentRail?.acceptACH === true}
              onChange={setTipCents}
            />
          ) : null}
          {(() => {
            const pay = invoice.payment;
            // Payabli-primary tenant: render the inline embedded component.
            // It shows its own unavailable notice when publicToken/entryPoint
            // are missing; if the whole payabli block is null, online pay
            // isn't set up for this tenant.
            if (pay?.processor === 'payabli') {
              // Not rendered over the cap: the embedded form's own submit
              // ignores `disabled` once it is open.
              if (tipOverLimit) return null;
              return pay.payabli ? (
                <PayabliInvoicePayment
                  invoiceId={id}
                  amount={payNow}
                  tipCents={tipCents}
                  config={pay.payabli}
                  contact={invoice.contact}
                  disabled={isSettlingPayment}
                />
              ) : (
                <PaymentUnavailableNotice />
              );
            }
            // Stripe, or a legacy/absent payment config (back-compat with
            // payloads that predate the processor block): hosted Checkout.
            if (!pay || pay.processor === 'stripe') {
              // Stripe Checkout charges the stored balance and cannot take a
              // partial beside a bank payment still processing.
              if (payNow < invoice.balanceDue) {
                return (
                  <p className="text-sm text-muted-foreground">
                    You can pay the rest online once your bank payment finishes processing.
                  </p>
                );
              }
              return (
                <InvoiceStripePayCTA
                  stripe={pay?.stripe}
                  principal={invoice.balanceDue}
                  total={invoice.balanceDue + tipCents / 100}
                  busy={payMutation.isPending || redirecting || isSettlingPayment}
                  disabled={tipOverLimit}
                  busyLabel={
                    redirecting
                      ? 'Redirecting to checkout…'
                      : isSettlingPayment
                        ? 'Finalizing payment…'
                        : null
                  }
                  onPay={(method) => {
                    if (tipOverLimit) {
                      toast.error(TIP_LIMIT_MESSAGE);
                      return;
                    }
                    payMutation.mutate({ tip: tipCents, method });
                  }}
                />
              );
            }
            // 'none' or an unsupported processor → no online payment path.
            return <PaymentUnavailableNotice />;
          })()}
        </>
      )}

      {isPaid && (
        <>
          <Separator />
          <div className="space-y-3 py-2">
            <div className="flex items-center justify-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              <span className="font-semibold">This invoice has been paid. Thank you!</span>
            </div>
            {/* Every payer reaches this block, not just the ones Stripe
                redirects back with `?payment=success`, so the charged total is
                stated here too — otherwise a Payabli payer is only ever told
                what the invoice was credited. */}
            <PublicInvoicePaidNote invoice={invoice} />
            {invoice.tipAmount > 0 ? (
              <p className="text-center text-sm text-muted-foreground">
                Tip received. Thank you for supporting your service provider.
              </p>
            ) : invoice.tipsEnabled ? (
              <Button asChild variant="outline" className="w-full">
                <a href={`/tip/${id}`}>
                  <HeartHandshake className="mr-2 h-4 w-4" />
                  Leave a tip
                </a>
              </Button>
            ) : null}
          </div>
        </>
      )}
    </>
  );

  const footerNote = hasCustomInvoiceFooterText ? (
    <>
      <Separator />
      <p className="text-center text-xs text-gray-400">{invoiceFooterText}</p>
    </>
  ) : (
    <>
      <Separator />
      <PoweredByNotifi className="flex" />
    </>
  );

  // App-owned: a customer can always download/print the invoice PDF (the same
  // design-aware document, via the public pdf-invoice route). Opens the viewer
  // in a new tab where the browser's own download/print controls take over.
  const downloadPdfButton = (
    <Button asChild variant="outline" className="w-full">
      <a href={`/pdf-invoice/${id}`} target="_blank" rel="noopener noreferrer">
        <Download className="mr-2 h-4 w-4" />
        Download PDF
      </a>
    </Button>
  );

  // ── Design branch — the hosted invoice rendered through the block engine ──
  // The document column is the design (letterhead, bill-to, line items, totals,
  // notes, terms — whatever the layout orders), matching the PDF byte for byte on
  // the figures. The job block and status chip are intentionally NOT carried here:
  // the invoice PDF design does not render them, and parity with the PDF is the
  // whole point of this branch.
  if (designLayout) {
    const designInvoice = toInvoice(invoice);
    const designCompany: PdfCompanyInfo = {
      name: invoice.business?.name ?? 'Your Company',
      logoUrl: invoice.business?.logo ?? null,
      primaryColor: invoice.business?.primaryColor ?? null,
      invoiceFooterText: invoice.business?.invoiceFooterText ?? null,
      address: businessAddress || null,
      email: invoice.business?.email ?? null,
      phone: invoice.business?.phone ?? null,
      website: invoice.business?.website ?? null,
      taxRegistrationNumber: invoice.business?.taxRegistrationNumber ?? null,
      contractorLicense: invoice.business?.contractorLicense ?? null,
      regulatoryNotice: invoice.business?.regulatoryNotice ?? null,
    };
    // The pay controls and the status alerts both live in the sidebar; a
    // terminal (void) invoice has neither pay controls nor an alert, so the
    // document then spans the full width with no empty rail.
    const hasPayArea = isPayable || isPaid;
    const hasChecklist = (invoice.checklistReports?.length ?? 0) > 0;
    return (
      <PublicLayout maxWidth="xl">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-8 lg:flex-row">
            {/* Actions sidebar — status alerts, the real Pay controls, and the
                PDF download. RIGHT at lg, BELOW when stacked. The alerts ride the
                rail beside the action they concern, instead of a full-width slab
                across the top. The design's payInvoice slot draws nothing; this
                owns the Pay button. Always rendered — a customer can download the
                PDF even on a paid or void invoice. */}
            <div className="order-2 mx-auto w-full min-w-0 max-w-xl flex-shrink-0 space-y-4 lg:sticky lg:top-8 lg:mx-0 lg:w-[360px] lg:self-start">
              {statusBanners}
              {hasPayArea ? (
                <Card className="shadow-sm">
                  <CardHeader className="pb-0">
                    <CardTitle className="text-base">Payment</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6 pt-0">{payPanel}</CardContent>
                </Card>
              ) : null}
              {downloadPdfButton}
            </div>
            {/* Document — SAME engine as the PDF, so the hosted link matches the
                download. Its own white sheet; app-owned checklist downloads sit
                beneath it. */}
            <div className="order-1 min-w-0 flex-1">
              <InvoiceDocumentRenderer
                layout={designLayout}
                invoice={designInvoice}
                company={designCompany}
                promotionName={invoice.promotionName}
                timeZone={timezone}
                renderSlots={false}
                afterTotals={financing}
              />
              {hasChecklist ? (
                <Card className="mx-auto mt-6 w-full max-w-[700px] shadow-sm">
                  <CardContent className="pt-0">{checklistSection}</CardContent>
                </Card>
              ) : null}
            </div>
          </div>
          <div className="text-center">{footerNote}</div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <Card className="overflow-hidden shadow-sm">
        {/* Header */}
        <CardHeader className="bg-white pb-4">
          <div className="flex min-w-0 items-start justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              {invoice.business?.logo ? (
                <img
                  src={invoice.business.logo}
                  alt={invoice.business.name}
                  className="h-12 w-12 shrink-0 rounded-lg object-contain"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gray-100">
                  <Building2 className="h-6 w-6 text-gray-400" />
                </div>
              )}
              <div className="min-w-0">
                <p className="break-words font-semibold text-gray-900">
                  {invoice.business?.name ?? 'Your Service Provider'}
                </p>
                <div className="mt-0.5 space-y-0.5 text-sm text-gray-500">
                  {businessAddress ? <p className="break-words">{businessAddress}</p> : null}
                  {invoice.business?.contractorLicense ? (
                    <p>License {invoice.business.contractorLicense}</p>
                  ) : null}
                  {invoice.business?.regulatoryNotice ? (
                    <p className="whitespace-pre-wrap break-words">
                      {invoice.business.regulatoryNotice}
                    </p>
                  ) : null}
                  {invoice.business?.phone ||
                  invoice.business?.email ||
                  invoice.business?.website ? (
                    <p className="flex flex-wrap gap-x-2 gap-y-0.5">
                      {invoice.business?.phone ? <span>{invoice.business.phone}</span> : null}
                      {invoice.business?.phone && invoice.business?.email ? <span>·</span> : null}
                      {invoice.business?.email ? (
                        <a
                          href={`mailto:${invoice.business.email}`}
                          className="min-w-0 break-words hover:text-gray-700"
                        >
                          {invoice.business.email}
                        </a>
                      ) : null}
                      {(invoice.business?.phone || invoice.business?.email) &&
                      invoice.business?.website ? (
                        <span>·</span>
                      ) : null}
                      {invoice.business?.website && businessWebsiteUrl ? (
                        <a
                          href={businessWebsiteUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="min-w-0 break-words hover:text-gray-700"
                        >
                          {invoice.business.website}
                        </a>
                      ) : null}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6 pt-4">
          {statusBanners}

          {/* Invoice meta */}
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <div>
              <p className="text-gray-500">Invoice #</p>
              <p className="font-medium text-gray-900">
                {invoice.invoiceNumber ?? id.slice(-6).toUpperCase()}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Date</p>
              <p className="font-medium text-gray-900">
                <PublicInvoiceIssueDate invoice={invoice} timezone={timezone} />
              </p>
            </div>
            {dueDate && (
              <div>
                <p className="text-gray-500">Due</p>
                <p className="font-medium text-gray-900">{formatDateOnly(invoice.dueDate)}</p>
              </div>
            )}
          </div>
          {timezoneLabel && <p className="text-xs text-gray-400">Times shown in {timezoneLabel}</p>}

          {(isClearing || invoice.status === 'partial' || isOverdue) && (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                isOverdue ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
              }`}
            >
              {isClearing ? 'Clearing' : isOverdue ? 'Overdue' : 'Partially paid'}
            </span>
          )}

          {billedToName && (
            <div className="text-sm">
              <p className="text-gray-500">Billed to</p>
              <p className="break-words font-medium text-gray-900">{billedToName}</p>
              {billedToCompany && <p className="break-words text-gray-600">{billedToCompany}</p>}
              {billedToEmail && <p className="break-words text-gray-600">{billedToEmail}</p>}
              {billedToAddress && <p className="break-words text-gray-600">{billedToAddress}</p>}
            </div>
          )}

          {invoice.job &&
          (invoice.job.jobNumber ||
            invoice.job.jobType ||
            invoice.job.serviceAddress ||
            jobAppointmentDates.length > 0) ? (
            <div className="rounded-lg bg-gray-50 px-4 py-3 text-sm">
              {invoice.job.jobNumber || invoice.job.jobType ? (
                <p className="font-medium text-gray-900">
                  {[invoice.job.jobType, invoice.job.jobNumber].filter(Boolean).join(' · ')}
                </p>
              ) : null}
              {invoice.job.serviceAddress ? (
                <p className="mt-0.5 text-gray-600">
                  Service address: {invoice.job.serviceAddress}
                </p>
              ) : null}
              {jobAppointmentDates.length > 0 ? (
                <p className="mt-0.5 text-gray-600">
                  Service {jobAppointmentDates.length === 1 ? 'date' : 'dates'}:{' '}
                  {jobAppointmentDates.map((date) => formatDocDate(date, timezone)).join(', ')}
                </p>
              ) : null}
            </div>
          ) : null}

          <Separator />

          {/* Line items */}
          <div>
            <CardTitle className="mb-4 flex items-center gap-2 text-base">
              <FileText className="h-4 w-4" />
              Services
            </CardTitle>
            <LineItemsTable
              items={invoice.lineItems}
              subtotal={invoice.subtotal}
              discountTotal={invoice.discountTotal}
              membershipDiscountTotal={invoice.membershipDiscountTotal}
              discountLabel={documentDiscountLabel({
                promotionName: invoice.promotionName,
              })}
              taxTotal={invoice.taxTotal}
              tipAmount={invoice.tipAmount}
              total={invoice.total}
              amountPaid={shown.paid ?? undefined}
              creditsApplied={shown.credits}
              balanceDue={displayedBalanceDue}
              paidFootnote={paidFootnote}
              depositApplied={invoice.depositApplied}
              depositServiceFeeAmount={invoice.depositServiceFeeAmount}
              paymentServiceFeeAmount={invoice.paymentServiceFeeAmount}
            />
          </div>

          {financing}

          {checklistSection}

          {invoice.customFields && invoice.customFields.length > 0 && (
            <>
              <Separator />
              <div className="text-sm">
                <p className="mb-2 font-medium text-gray-700">Additional Details</p>
                <dl className="space-y-2">
                  {invoice.customFields.map((field) => (
                    <div key={field.fieldDefinitionId} className="flex gap-2">
                      <dt className="font-medium text-gray-700">{field.label}:</dt>
                      <dd className="text-gray-600">{field.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </>
          )}

          {/* Notes / Terms */}
          {invoice.notes && (
            <>
              <Separator />
              <div className="text-sm">
                <p className="mb-1 font-medium text-gray-700">Notes</p>
                <p className="whitespace-pre-wrap font-sans text-gray-600">
                  {isHtmlContent(invoice.notes) ? stripHtml(invoice.notes) : invoice.notes}
                </p>
              </div>
            </>
          )}
          <InvoiceTermsSection terms={terms} />

          {payPanel}

          <Separator />
          {downloadPdfButton}

          {footerNote}
        </CardContent>
      </Card>
    </PublicLayout>
  );
}

/**
 * Terms section as the customer reads it on the public invoice — heading
 * included, so "no terms survived sanitizing" hides the heading too rather than
 * leaving a bare "Terms & Conditions" over nothing.
 *
 * Exported for tests: the TanStack router plugin rewrites route components in
 * the vitest pipeline, so the page can't be rendered directly (same reason
 * quote.$id.tsx exports EstimateView).
 */
/**
 * The Stripe hosted-Checkout CTA on the public invoice.
 *
 * Exported so the rail decisions can be tested directly — the page component
 * itself is router-bound. THE decision it makes: which rails the customer is
 * offered, and therefore which fee presentation applies.
 *
 * - Both rails accepted  → a Card | Bank account switch and one Pay button whose
 *   price follows it (the same choice the Payabli form offers). This is keyed
 *   on the tenant's accept flags ALONE. It used to require an enabled card
 *   surcharge, which meant a tenant accepting bank transfers without charging a
 *   card fee — the default configuration — rendered a single card-labelled
 *   button and the customer had no way to pay by ACH at all.
 * - Card only + surcharge → one CTA whose total INCLUDES the fee, because the
 *   session is deterministic ("Pay $108" charging $111 is a disclosure bug).
 * - Bank only            → one CTA that says bank transfer and sends 'ach'.
 *
 * ACH is never surcharged on any branch.
 */
export function InvoiceStripePayCTA({
  stripe,
  total,
  principal,
  busy,
  disabled = false,
  busyLabel,
  onPay,
}: {
  stripe: NonNullable<PublicInvoice['payment']>['stripe'];
  /** Balance due plus any pre-payment tip, in dollars. Never includes a fee. */
  total: number;
  /** Principal only; excludes the tip already included in total. */
  principal?: number;
  busy: boolean;
  /** Blocks paying without the busy spinner (e.g. a tip over the cap). */
  disabled?: boolean;
  busyLabel: string | null;
  onPay: (method?: 'card' | 'ach') => void;
}) {
  // Card first, as on the Payabli form. The rail is chosen here, not in
  // Checkout: the session's amount depends on it (a card fee or a bank discount).
  const [rail, setRail] = useState<'card' | 'ach'>('card');
  const discount = computePaymentMethodDiscount(
    stripe?.surcharge,
    Math.round((principal ?? total) * 100),
    'ACH',
  );
  const discountCents = discount.enabled
    ? (stripe?.surcharge?.discountCents ?? discount.discountCents)
    : 0;
  const achSavings =
    stripe?.acceptACH && discount.enabled && discountCents > 0 ? (
      <AchSavingsBadge
        discountCents={discountCents}
        percent={discount.percent}
        hasTip={Math.round(total * 100) > Math.round((principal ?? total) * 100)}
      />
    ) : null;
  const achTotal = total - discountCents / 100;
  const feeCents = discount.enabled ? 0 : (stripe?.surcharge?.feeCents ?? 0);
  // A fee only exists when the tenant surcharges AND the computed fee is
  // non-zero; it may only ever ride a card-ONLY session.
  const feeApplies = stripe?.surcharge?.enabled === true && feeCents > 0;
  const discountPercent = effectivePaymentMethodDiscountPercent(
    total,
    feeCents / 100,
    stripe?.surcharge?.surchargePercent,
  );

  if (stripe?.acceptCard && stripe.acceptACH) {
    const cardTotal = total + (feeApplies ? feeCents / 100 : 0);
    return (
      <div className="space-y-4">
        <PaymentRailSwitch
          value={rail}
          onChange={setRail}
          disabled={busy || disabled}
          achSavings={busy || busyLabel ? null : achSavings}
          testIdPrefix="invoice-rail"
        />
        <CustomerPayButton
          label={busyLabel ?? `Pay ${formatMoney(rail === 'ach' ? achTotal : cardTotal)}`}
          busy={busy}
          disabled={disabled}
          onClick={() => onPay(rail)}
          secureNote="Secure checkout by Stripe"
          testId={`invoice-pay-${rail}`}
        />
        {feeApplies ? (
          <p className="text-center text-xs text-emerald-700">
            Save {formatMoney(feeCents / 100)} ({formatDiscountPercent(discountPercent)}) by bank
            transfer or check.
          </p>
        ) : null}
      </div>
    );
  }

  // The dual-rail branch above owns "both accepted", so reaching here means at
  // most ONE rail. Card-only + surcharge ⇒ the session is card-only and the
  // button total must include the fee.
  const cardOnlyStripeFee = stripe?.surcharge?.enabled && !stripe.acceptACH ? feeCents / 100 : 0;
  // Bank transfer is the only rail: say so, and send the method explicitly
  // rather than showing a card icon over a checkout that can only take a bank
  // account.
  const bankOnly = stripe?.acceptACH === true && stripe?.acceptCard === false;
  return (
    <>
      <Button
        className={`w-full ${bankOnly && achSavings ? 'h-auto min-h-11 flex-col gap-1.5 whitespace-normal py-3' : ''}`}
        size="lg"
        onClick={() => onPay(bankOnly ? 'ach' : undefined)}
        disabled={busy || disabled}
        data-testid="invoice-pay-single"
      >
        <span className="flex items-center justify-center">
          {busy ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : bankOnly ? (
            <Landmark className="mr-2 h-4 w-4" />
          ) : (
            <CreditCard className="mr-2 h-4 w-4" />
          )}
          {busyLabel ??
            `${bankOnly ? 'Pay by bank transfer' : 'Pay'} ${formatMoney(bankOnly ? achTotal : total + cardOnlyStripeFee)}`}
        </span>{' '}
        {bankOnly && !busy && !busyLabel ? achSavings : null}
      </Button>
      {cardOnlyStripeFee > 0 ? (
        <p className="text-center text-xs text-gray-500">
          {cardServiceFeeNote({
            surchargePercent: stripe?.surcharge?.surchargePercent ?? 0,
            feeAmount: formatMoney(cardOnlyStripeFee),
            achOrCheckPrice: total,
            cardPriceDifference: cardOnlyStripeFee,
            achOffered: false,
            // Stripe Checkout charges this total; a card that settles as
            // non-credit has the fee refunded by `reverseNonCreditCardSurcharge`.
            creditCardOnly: false,
          })}
        </p>
      ) : null}
      <p className="text-center text-xs text-gray-400">Secured by Stripe</p>
    </>
  );
}

export function InvoiceTermsSection({ terms }: { terms: ResolvedCustomerTerms }) {
  if (!terms.hasTerms) return null;

  return (
    <div className="text-sm">
      <p className="mb-1 font-medium text-gray-700">Terms &amp; Conditions</p>
      <CustomerTermsBody terms={terms} className="text-gray-600" />
    </div>
  );
}

function WisetackInvoiceCta({ wisetack }: { wisetack: PublicWisetackCta }) {
  return (
    <section aria-label="Wisetack financing" className="space-y-3" data-testid="wisetack-cta">
      <div className="flex items-start gap-3">
        <img
          src="/brand/integrations/wisetack.png"
          alt="Wisetack"
          className="mt-0.5 size-8 shrink-0 object-contain"
        />
        <div className="min-w-0">
          {/* Space navy from Wisetack's brand palette (wisetack.com/brand). */}
          <h3 className="text-sm font-semibold" style={{ color: WISETACK_BRAND.space }}>
            Wisetack financing
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Prequalify to see available payment options for this invoice.
          </p>
        </div>
      </div>
      {/* Sapphire primary CTA per Wisetack's button spec. */}
      <Button
        asChild
        className="w-full text-white hover:opacity-90"
        style={{ backgroundColor: WISETACK_BRAND.sapphire }}
      >
        <a href={wisetack.ctaUrl} target="_blank" rel="noreferrer">
          See Financing Options
          <ExternalLink className="size-4" />
        </a>
      </Button>
      <WisetackDisclosure
        disclosure={wisetack.disclosure}
        className="text-[11px] leading-4 text-gray-500"
      />
    </section>
  );
}
